import { For, Show, createEffect, createMemo, createSignal, on } from "solid-js";
import { CheckMenu } from "../../components/CheckMenu.tsx";
import { ChevronDownIcon, ChevronRightIcon, NoteIcon } from "../../components/icons.tsx";
import { RadarChart, type RadarAxis, type RadarSeries } from "../../components/RadarChart.tsx";
import { Button, SelectField } from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { formatScore, formatWeight } from "../../lib/format.ts";
import {
  lensCandidates,
  nearestRating,
  ownPersonas,
  personaLens,
  shiftOf,
  toneOf,
  weightFactor,
  type PersonaLens,
  type Shift,
} from "./compare-lens.ts";
import {
  biggestDifferences,
  breakdown,
  byImpact,
  compareAxes,
  divergence,
  comparableProducts,
  explanationOf,
  questionScope,
  inCatalogueOrder,
  mostDivergent,
  oneSidedAxes,
  openingPair,
  overallScore,
  sameAxes,
  scoreMax,
  tiedAxes,
  type CompareAxis,
  type CompareDifference,
  type CompareLevel,
  type QuestionScope,
} from "./compare.ts";
import { QuestionDialog } from "./QuestionDialog.tsx";
import type { ReportSnapshot } from "../../api/domain.ts";
import type { CompareSelection } from "./compare-url.ts";
import styles from "./ProductCompare.module.scss";

export type ProductCompareProps = {
  snapshot: ReportSnapshot;
  /** What the reader has pinned down; the page keeps it in the URL. */
  selection: CompareSelection;
  onChange: (selection: CompareSelection) => void;
  /**
   * The customer the reader belongs to, as a member or in preview. Their own
   * persona's weighting is then read in without asking; a consultant, who has
   * none, picks a persona explicitly.
   */
  customer?: string;
};

/**
 * Two products face to face on one wheel: where each is strong, where it is
 * thin, and where they genuinely differ.
 *
 * The axes open on the topics the pair disagrees on most, because that is the
 * only part of a thirty-two-topic catalogue a comparison is actually about. The
 * reader can widen or narrow that, and from the moment they do, the selection
 * is theirs and stops moving under them.
 *
 * The component owns no state: every choice goes up through `onChange`, so the
 * page can write it to the URL and hand it back after a reload.
 */
export const ProductCompare = (props: ProductCompareProps) => {
  const i18n = useI18n();

  const products = createMemo(() => comparableProducts(props.snapshot));
  const opening = createMemo(() => openingPair(props.snapshot));

  const change = (patch: Partial<CompareSelection>) =>
    props.onChange({ ...props.selection, ...patch });

  /** A pinned product that this evaluation does not know falls back to the automatic pick. */
  const known = (id: string | null): string | null =>
    id !== null && products().some((product) => product.id === id) ? id : null;

  const level = () => props.selection.level;
  const chosen = () => props.selection.axes;
  const left = () => known(props.selection.a) ?? opening()?.a ?? "";
  const right = () => known(props.selection.b) ?? opening()?.b ?? "";

  const axes = createMemo(() => compareAxes(props.snapshot, level(), left(), right()));

  /** The automatic pick, until the reader edits it; then their list wins. */
  const picked = createMemo(() => chosen() ?? mostDivergent(axes()));
  /*
   * Every selection change rebuilds these arrays, so the memo is told to compare
   * contents. Without it, folding one row open replaces every row in the table
   * and the button the reader just pressed is gone from under their focus.
   */
  const shown = createMemo(() => inCatalogueOrder(axes(), picked()), [], {
    equals: sameAxes,
  });

  /** The same axes the wheel draws, ranked by how far apart the pair stands. */
  const differences = createMemo(() => biggestDifferences(shown()));
  const tied = createMemo(() => tiedAxes(shown()));
  const oneSided = createMemo(() => oneSidedAxes(shown()));

  const setA = (a: string) => change({ a });
  const setB = (b: string) => change({ b });
  const setChosen = (axes: string[] | null) => change({ axes });

  /** A new level offers a different set of axes, so the old pick cannot carry over. */
  const chooseLevel = (next: CompareLevel) => change({ level: next, axes: null, open: [] });

  const swap = () => change({ a: right(), b: left() });

  const isOpen = (axis: string) => props.selection.open.includes(axis);

  /** Folding is the reader's, so it rides in the URL with everything else. */
  const toggle = (axis: string) =>
    change({
      open: isOpen(axis)
        ? props.selection.open.filter((entry) => entry !== axis)
        : [...props.selection.open, axis],
    });

  const nameOf = (id: string) => products().find((product) => product.id === id)?.label ?? id;

  /*
   * The question the reader has opened to read its reasoning, and from which
   * side. It is not in the URL: a dialog is a glance, not a place to come back to.
   */
  const [inspected, setInspected] = createSignal<{
    capability: string;
    question: string;
    side: "a" | "b";
  } | null>(null);

  const inspectedScope = createMemo(() => {
    const at = inspected();
    return at === null
      ? undefined
      : questionScope(props.snapshot, at.capability, at.question, left(), right());
  });

  /* A new pair or level is a new conversation; a dialog about the old one closes. */
  createEffect(on([left, right, level], () => setInspected(null), { defer: true }));

  /*
   * Whose weighting the page is read through. A customer's own personas come
   * first, and with one of them there is no choice to make; a consultant
   * chooses from every persona the snapshot was evaluated for, or none.
   */
  const own = createMemo(() => ownPersonas(props.snapshot, props.customer));
  const candidates = createMemo(() => (own().length > 0 ? own() : lensCandidates(props.snapshot)));
  const personaId = createMemo((): string | undefined => {
    const asked = props.selection.persona;
    if (asked !== null && candidates().some((persona) => persona.id === asked)) return asked;
    return own()[0]?.id;
  });
  const lens = createMemo((): PersonaLens | undefined => {
    const id = personaId();
    return id === undefined ? undefined : personaLens(props.snapshot, id);
  });
  const setPersona = (persona: string) => change({ persona: persona === "" ? null : persona });

  /** The persona's weight on one axis, as the wheel and the table draw it. */
  const markOf = (axis: CompareAxis): RadarAxis["mark"] => {
    const through = lens();
    if (through === undefined || !through.rated) return undefined;
    const weight = through.weightOf(level(), axis.id);
    if (weight === null) return undefined;
    return {
      tone: toneOf(weight),
      label: `${i18n.t().compare.weightOf} ${through.name}: ${
        i18n.t().relevancy.scale[nearestRating(weight)]
      } · ${i18n.t().relevancy.weight} ${formatWeight(weight, i18n.locale())}`,
      muted: weight === 0,
    };
  };

  const markedAxes = createMemo((): RadarAxis[] =>
    shown().map((axis) => ({ id: axis.id, label: axis.label, mark: markOf(axis) })),
  );

  const legend = (id: string) => {
    const score = overallScore(props.snapshot, id);
    return score === null
      ? nameOf(id)
      : `${nameOf(id)} · ${formatScore(score, i18n.locale())}`;
  };

  /** The persona's own total beside the global one, and which way it moved. */
  const weightedDetail = (id: string) => {
    const through = lens();
    if (through === undefined || !through.rated) return undefined;
    const weighted = through.weightedScore(id);
    if (weighted === null) return undefined;
    return (
      <WeightedTotal
        persona={through.name}
        value={weighted}
        shift={shiftOf(overallScore(props.snapshot, id), weighted)}
      />
    );
  };

  const series = createMemo<RadarSeries[]>(() => [
    {
      id: left(),
      label: legend(left()),
      color: "var(--compare-a)",
      fill: "var(--compare-a-fill)",
      marker: "circle",
      values: shown().map((axis) => axis.a),
      detail: weightedDetail(left()),
    },
    {
      id: right(),
      label: legend(right()),
      color: "var(--compare-b)",
      fill: "var(--compare-b-fill)",
      marker: "square",
      values: shown().map((axis) => axis.b),
      detail: weightedDetail(right()),
    },
  ]);

  const levels: { id: CompareLevel; label: string }[] = [
    { id: "topic", label: i18n.t().compare.levelTopic },
    { id: "capability", label: i18n.t().compare.levelCapability },
  ];

  return (
    <Show when={products().length >= 2}>
      <Panel title={i18n.t().compare.title}>
        <div class={styles.controls}>
          <div class={styles.pair}>
            <div class={`${styles.pick} ${styles.pickA}`}>
              <SelectField
                label={i18n.t().compare.productA}
                choices={products()}
                value={left()}
                onPick={setA}
              />
            </div>
            <Button label={i18n.t().compare.swap} onClick={swap} />
            <div class={`${styles.pick} ${styles.pickB}`}>
              <SelectField
                label={i18n.t().compare.productB}
                choices={products()}
                value={right()}
                onPick={setB}
              />
            </div>
          </div>

          <div class={styles.filters}>
            <div class={styles.levels} role="group" aria-label={i18n.t().compare.level}>
              {levels.map((entry) => (
                <button
                  class={`${styles.level} ${level() === entry.id ? styles.levelActive : ""}`}
                  type="button"
                  aria-pressed={level() === entry.id}
                  onClick={() => chooseLevel(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>

            <CheckMenu
              label={i18n.t().compare.axes}
              options={axes().map((axis) => ({
                id: axis.id,
                label: axis.label,
                group: level() === "capability" ? axis.group : undefined,
              }))}
              picked={picked()}
              onChange={setChosen}
              allLabel={i18n.t().compare.allAxes}
              noneLabel={i18n.t().compare.noAxes}
              emptyLabel={i18n.t().compare.noAxesAvailable}
            />

            <Show when={chosen() !== null}>
              <button class={styles.reset} type="button" onClick={() => setChosen(null)}>
                {i18n.t().compare.reset}
              </button>
            </Show>
          </div>
        </div>

        {/*
          One persona of one's own needs no control; it is simply how this reader
          sees the world, and a line says so. Anyone with a choice gets the picker.
        */}
        <Show when={candidates().length > 0}>
          <div class={styles.lensRow}>
            <Show
              when={own().length !== 1}
              fallback={
                <p class={styles.lensNote}>
                  <span class={styles.lensSwatch} aria-hidden="true" />
                  {i18n.t().compare.weightedFor} <strong>{lens()?.name}</strong>
                </p>
              }
            >
              <div class={styles.lensPick}>
                <SelectField
                  label={i18n.t().compare.persona}
                  hint={i18n.t().compare.personaHint}
                  placeholder={i18n.t().compare.personaNone}
                  optional={own().length === 0}
                  choices={candidates().map((persona) => ({ id: persona.id, label: persona.name }))}
                  value={personaId() ?? ""}
                  onPick={setPersona}
                />
              </div>
            </Show>
            <Show when={lens() !== undefined && !(lens()?.rated ?? false)}>
              <p class={styles.lensNote}>
                <strong>{lens()?.name}</strong> {i18n.t().compare.notRated}
              </p>
            </Show>
          </div>
        </Show>

        <Show
          when={left() !== right()}
          fallback={<p class={styles.note}>{i18n.t().compare.samePair}</p>}
        >
          <RadarChart
            axes={markedAxes()}
            series={series()}
            max={scoreMax}
            format={(value) => formatScore(value, i18n.locale())}
            missingLabel={i18n.t().common.notAssessed}
            axisHeader={
              level() === "topic" ? i18n.t().compare.levelTopic : i18n.t().compare.levelCapability
            }
            markHeader={i18n.t().compare.weightHeader}
            caption={i18n.t().compare.tableCaption}
            emptyLabel={i18n.t().compare.noAxesPicked}
          />

          <Show when={shown().length > 0}>
            <section class={styles.differences}>
              <div class={styles.differencesHead}>
                <h3 class={styles.differencesTitle}>{i18n.t().compare.differences}</h3>
                <p class={styles.differencesIntro}>{i18n.t().compare.differencesIntro}</p>
                <Show when={lens()?.rated}>
                  <p class={`${styles.differencesIntro} ${styles.keyLine}`}>
                    <span class={styles.keySwatch} aria-hidden="true" />
                    {i18n.t().compare.weightedGapKey}
                  </p>
                </Show>
              </div>

              <Show
                when={differences().length > 0}
                fallback={<p class={styles.note}>{i18n.t().compare.noDifferences}</p>}
              >
                <table class={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col" class={styles.axisHead}>
                        {level() === "topic"
                          ? i18n.t().compare.levelTopic
                          : i18n.t().compare.levelCapability}
                      </th>
                      <th scope="col" class={styles.scoreHead}>
                        <span class={`${styles.swatch} ${styles.swatchA}`} aria-hidden="true" />
                        {nameOf(left())}
                      </th>
                      <th scope="col" class={styles.scoreHead}>
                        <span class={`${styles.swatch} ${styles.swatchB}`} aria-hidden="true" />
                        {nameOf(right())}
                      </th>
                      <th scope="col" class={styles.gapHead}>
                        {i18n.t().compare.gap}
                      </th>
                    </tr>
                  </thead>
                  <For each={differences()}>
                    {(axis) => {
                      const parts = createMemo(() =>
                        byImpact(breakdown(props.snapshot, level(), axis.id, left(), right())),
                      );
                      const open = () => isOpen(axis.id);

                      return (
                        <tbody class={styles.group}>
                          <tr class={styles.row}>
                            <th scope="row" class={styles.axis}>
                              <button
                                class={styles.disclosure}
                                type="button"
                                aria-expanded={open()}
                                onClick={() => toggle(axis.id)}
                              >
                                <span class={styles.twisty} aria-hidden="true">
                                  <Show when={open()} fallback={<ChevronRightIcon />}>
                                    <ChevronDownIcon />
                                  </Show>
                                </span>
                                <span class={styles.axisLabel}>{axis.label}</span>
                              </button>
                              <Show when={level() === "capability"}>
                                <span class={styles.axisGroup}>{axis.group}</span>
                              </Show>
                            </th>
                            <Score value={axis.a} leading={axis.leader === "a"} />
                            <Score value={axis.b} leading={axis.leader === "b"} />
                            <td class={styles.gapCell}>
                              <Meter
                                gap={axis.gap}
                                leader={axis.leader}
                                weight={lens()?.rated ? lens()?.weightOf(level(), axis.id) ?? undefined : undefined}
                                weightLabel={markOf(axis)?.label}
                              />
                            </td>
                          </tr>

                          {/*
                            The parts stay in the same table so their columns keep
                            the parent's edges, and in their own row group so the
                            rule under the last of them closes the whole entry.
                          */}
                          <tr class={styles.partsHead} hidden={!open()}>
                            <th scope="colgroup" colSpan={4}>
                              {level() === "topic"
                                ? i18n.t().compare.partsCapabilities
                                : i18n.t().compare.partsQuestions}
                            </th>
                          </tr>

                          <Show when={parts().length > 0}>
                            <For each={parts()}>
                              {(part) => (
                                <Part
                                  part={part}
                                  hidden={!open()}
                                  scope={
                                    level() === "capability"
                                      ? questionScope(props.snapshot, axis.id, part.id, left(), right())
                                      : undefined
                                  }
                                  nameA={nameOf(left())}
                                  nameB={nameOf(right())}
                                  onInspect={(side) =>
                                    setInspected({ capability: axis.id, question: part.id, side })
                                  }
                                />
                              )}
                            </For>
                          </Show>

                          <Show when={parts().length === 0}>
                            <tr class={styles.partRow} hidden={!open()}>
                              <td class={styles.partEmpty} colSpan={4}>
                                {i18n.t().compare.noParts}
                              </td>
                            </tr>
                          </Show>
                        </tbody>
                      );
                    }}
                  </For>
                </table>
              </Show>

              <Show when={tied() > 0 || oneSided() > 0}>
                <p class={styles.footnote}>
                  <Show when={tied() > 0}>
                    <span>
                      {i18n.t().compare.tiedCount}: {tied()}
                    </span>
                  </Show>
                  <Show when={oneSided() > 0}>
                    <span>
                      {i18n.t().compare.oneSidedCount}: {oneSided()}
                    </span>
                  </Show>
                </p>
              </Show>
            </section>
          </Show>
        </Show>
      </Panel>

      <QuestionDialog
        scope={inspectedScope()}
        side={inspected()?.side ?? "a"}
        nameA={nameOf(left())}
        nameB={nameOf(right())}
        onClose={() => setInspected(null)}
      />
    </Show>
  );
};

/**
 * One product's score in a row, at either level of the list.
 *
 * The leading side is set in the strong weight and carries a word a screen
 * reader can hear, because a reader who cannot see which number is heavier
 * should not have to subtract two figures to learn who is ahead. A side with no
 * score says so rather than showing a zero it was never given.
 */
const Score = (props: {
  value: number | null;
  leading: boolean;
  part?: boolean;
  /**
   * Set when the author wrote down why this value is what it is. The number
   * then carries a small mark that opens the reasoning; a value without one
   * stays a bare number, so the marks are a signal rather than a texture.
   */
  note?: { label: string; onClick: () => void };
}) => {
  const i18n = useI18n();
  const classes = () =>
    [styles.score, props.part === true ? styles.partScore : "", props.leading ? styles.leading : ""]
      .filter((name) => name !== "")
      .join(" ");

  return (
    <td class={classes()}>
      <Show
        when={props.value !== null}
        fallback={<span class={styles.absent}>{i18n.t().common.notAssessed}</span>}
      >
        <span class={styles.scoreInner}>
          <Show when={props.leading}>
            <span class={styles.spoken}>{i18n.t().compare.leads}: </span>
          </Show>
          {formatScore(props.value ?? 0, i18n.locale())}
          <Show when={props.note}>
            {(note) => (
              <button
                class={styles.mark}
                type="button"
                title={note().label}
                aria-label={note().label}
                onClick={note().onClick}
              >
                <NoteIcon />
              </button>
            )}
          </Show>
        </span>
      </Show>
    </td>
  );
};

/**
 * The distance between the two sides, as a bar and a number.
 *
 * The bar leaves the centre towards whichever side is ahead, so a column of
 * rows reads as a shape before a single number has been read.
 */
const Meter = (props: {
  gap: number;
  leader: "a" | "b";
  part?: boolean;
  /**
   * The persona's weight on this axis, 0 to 1. Draws a second, thinner bar
   * under the first with the distance scaled the way the engine scales it: a
   * `=` weight leaves it as it is, `++` doubles it, `--` takes it to nothing.
   */
  weight?: number;
  weightLabel?: string;
}) => {
  const i18n = useI18n();
  const half = (gap: number) => `${(Math.min(gap, scoreMax) / scoreMax) * 50}%`;
  const weighted = () => (props.weight === undefined ? undefined : props.gap * weightFactor(props.weight));

  return (
    <span class={styles.gapInner}>
      <span
        class={props.part === true ? `${styles.meter} ${styles.partMeter}` : styles.meter}
        aria-hidden="true"
      >
        <span
          class={`${styles.fill} ${props.leader === "a" ? styles.fillA : styles.fillB}`}
          style={{ width: half(props.gap) }}
        />
        <Show when={weighted() !== undefined}>
          <span
            class={`${styles.fill} ${styles.fillWeighted} ${props.leader === "a" ? styles.fillA : styles.fillB}`}
            style={{ width: half(weighted() ?? 0) }}
          />
        </Show>
      </span>
      <Show when={weighted() !== undefined}>
        <span class={styles.spoken}>
          {i18n.t().compare.weightedGap}: {formatScore(weighted() ?? 0, i18n.locale())}.{" "}
          {props.weightLabel}
        </span>
      </Show>
      <span class={props.part === true ? `${styles.gapValue} ${styles.partGap}` : styles.gapValue}>
        {formatScore(props.gap, i18n.locale())}
      </span>
    </span>
  );
};

/** Where a part carries no distance: one side missing, or the two level. */
const PartNote = (props: { part: CompareAxis }) => {
  const i18n = useI18n();
  const missing = () => props.part.a === null || props.part.b === null;

  return (
    <span class={styles.partNote}>
      {missing() ? i18n.t().compare.partOneSided : i18n.t().compare.partTied}
    </span>
  );
};

/**
 * One question or capability an axis is made of.
 *
 * At question level each side's number knows the answer behind it, and where
 * the author explained that answer the number carries a mark that opens it.
 */
const Part = (props: {
  part: CompareAxis;
  hidden: boolean;
  /** The question behind the row with both answers; undefined for a capability row. */
  scope?: QuestionScope;
  nameA: string;
  nameB: string;
  onInspect: (side: "a" | "b") => void;
}) => {
  const i18n = useI18n();
  const gap = () => divergence(props.part);

  const noteFor = (side: "a" | "b") => {
    const scope = props.scope;
    if (scope === undefined || explanationOf(scope[side]) === null) return undefined;
    return {
      label: `${i18n.t().compare.showExplanation}: ${side === "a" ? props.nameA : props.nameB}`,
      onClick: () => props.onInspect(side),
    };
  };
  /** Null wherever there is no distance to lead by: a missing side, or a tie. */
  const leader = (): "a" | "b" | null => {
    const distance = gap();
    if (distance === null || distance === 0) return null;
    return (props.part.a ?? 0) > (props.part.b ?? 0) ? "a" : "b";
  };

  return (
    <tr class={styles.partRow} hidden={props.hidden}>
      <th scope="row" class={styles.partAxis}>
        <span class={styles.partLabel}>{props.part.label}</span>
      </th>
      <Score value={props.part.a} leading={leader() === "a"} part note={noteFor("a")} />
      <Score value={props.part.b} leading={leader() === "b"} part note={noteFor("b")} />
      <td class={`${styles.gapCell} ${styles.partGapCell}`}>
        <Show when={leader() !== null} fallback={<PartNote part={props.part} />}>
          <Meter gap={gap() ?? 0} leader={leader() ?? "a"} part />
        </Show>
      </td>
    </tr>
  );
};

/**
 * A product's total as the persona weighs it, beside the global one in the
 * legend. The triangle says which way the weighting moved it, and a word says
 * the same for a reader who gets neither the shape nor the colour.
 */
const WeightedTotal = (props: { persona: string; value: number; shift: Shift }) => {
  const i18n = useI18n();
  const spoken = () =>
    props.shift === "up"
      ? i18n.t().compare.weightedUp
      : props.shift === "down"
        ? i18n.t().compare.weightedDown
        : i18n.t().compare.weightedLevel;

  return (
    <span class={`${styles.weighted} ${styles[`shift${props.shift}`]}`}>
      <span class={styles.weightedFor}>
        {i18n.t().compare.weightedFor} {props.persona}
      </span>
      <ShiftIcon shift={props.shift} />
      <span class={styles.weightedValue}>{formatScore(props.value, i18n.locale())}</span>
      <span class={styles.spoken}>, {spoken()}</span>
    </span>
  );
};

/** Up, down, or a dash for level: drawn, so it carries the tone's colour cleanly. */
const ShiftIcon = (props: { shift: Shift }) => (
  <svg class={styles.shiftIcon} viewBox="0 0 10 10" aria-hidden="true">
    <Show when={props.shift === "up"}>
      <path d="M5 1.5 9 8.5H1Z" />
    </Show>
    <Show when={props.shift === "down"}>
      <path d="M5 8.5 1 1.5h8Z" />
    </Show>
    <Show when={props.shift === "level"}>
      <rect x="1" y="4.25" width="8" height="1.5" rx="0.75" />
    </Show>
  </svg>
);
